import React, { useState, useEffect, useRef } from 'react';
import {
  Globe, Hash, FileText, Wallet, Package, ShoppingCart,
  CreditCard, ShieldCheck, Save, Search,
  ChevronDown, Check, Settings, Star, Info, X, RefreshCw,
  Users, Clock, Send, Plus, Trash2, Edit2
} from 'lucide-react';

import api from '../../services/api';
import { Toast } from '../ui/Toast';
import { useLoader } from '../../contexts/LoaderContext';
import { useParamsCompta } from '../../contexts/ParametresComptaContext';

// ============================================================================
// SECTIONS
// ============================================================================
const SECTIONS = [
  { id: 'general', title: 'Général', subtitle: 'Devise, exercice comptable et format d\'affichage', icon: Globe, color: '#448696' },
  { id: 'numerotation', title: 'Numérotation', subtitle: 'Préfixes et format des documents', icon: Hash, color: '#6366f1' },
  { id: 'facturation', title: 'Facturation', subtitle: 'TVA, délais de paiement et mentions légales', icon: FileText, color: '#f97316' },
  { id: 'tresorerie', title: 'Trésorerie', subtitle: 'Règles des caisses et transferts', icon: Wallet, color: '#10b981' },
  { id: 'stocks', title: 'Stocks', subtitle: 'Alertes, méthode de valorisation et règles', icon: Package, color: '#8b5cf6' },
  { id: 'commandes', title: 'Commandes', subtitle: 'Workflow de validation et relances', icon: ShoppingCart, color: '#ec4899' },
  { id: 'paie', title: 'Paie & RH', subtitle: 'Cotisations sociales et avances', icon: CreditCard, color: '#448696' },
  { id: 'groupes_salariaux', title: 'Groupes salariaux', subtitle: 'Taux horaires et catégories d\'employés', icon: Users, color: '#448696' },
  { id: 'deductions_retard', title: 'Déductions retard', subtitle: 'Paliers de retenue sur taux horaire', icon: Clock, color: '#f97316' },
  { id: 'envoi_compta', title: 'Envoi comptabilité', subtitle: 'Automatisation et règles d\'envoi', icon: Send, color: '#10b981' },
  { id: 'validation', title: 'Validation', subtitle: 'Seuils d\'approbation et notifications', icon: ShieldCheck, color: '#14b8a6' },
];

const METHODES_VALORISATION = [
  { value: 'cmup', label: 'CMUP — Coût Moyen Unitaire Pondéré' },
  { value: 'fifo', label: 'FIFO — Premier Entré, Premier Sorti' },
  { value: 'dernier_prix', label: 'Dernier prix d\'achat' },
];

const ParametresComptabilite = () => {
  const [params, setParams] = useState({});
  const [devises, setDevises] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [activeSection, setActiveSection] = useState('general');
  const [hasChanges, setHasChanges] = useState(false);

  // Groupes salariaux
  const [groupes, setGroupes] = useState([]);
  const [loadingGroupes, setLoadingGroupes] = useState(false);
  const [editingGroupe, setEditingGroupe] = useState(null);
  const [newGroupe, setNewGroupe] = useState({ nom: '', description: '', taux_horaire: '', heures_journalieres: 8 });
  const [showNewGroupe, setShowNewGroupe] = useState(false);

  // Paliers déduction retard
  const [paliers, setPaliers] = useState([]);
  const [newPalier, setNewPalier] = useState({ label: '', statut_ponctualite: '', type_deduction: 'montant_fixe', valeur: '' });
  const [showNewPalier, setShowNewPalier] = useState(false);

  // Devise dropdown
  const [showDeviseDropdown, setShowDeviseDropdown] = useState(false);
  const [deviseSearch, setDeviseSearch] = useState('');
  const dropdownRef = useRef(null);

  const { showLoader, hideLoader } = useLoader();
 const { refresh: refreshContext, symbole } = useParamsCompta();
  const showToast = (m, t = 'success') => setToast({ show: true, message: m, type: t });

  useEffect(() => {
    if (toast.show) { const t = setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000); return () => clearTimeout(t); }
  }, [toast.show]);

  useEffect(() => { loadAll(); }, []);


  useEffect(() => {
    if (activeSection === 'groupes_salariaux') loadGroupes();
    if (activeSection === 'deductions_retard') loadPaliers();
  }, [activeSection]);



  // Fermer dropdown devise au clic extérieur
  useEffect(() => {
    const handler = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowDeviseDropdown(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const loadAll = async () => {
    showLoader(); setIsLoading(true);
    try {
      await api.parametresCompta.init();
      const [pRes, dRes] = await Promise.all([
        api.parametresCompta.getAll(),
        api.parametresCompta.getDevises(),
      ]);
      if (pRes.success) {
        const flat = {};
        for (const [, values] of Object.entries(pRes.data || {})) {
          for (const [key, val] of Object.entries(values)) flat[key] = val;
        }
        setParams(flat);
      }
      if (dRes.success) setDevises(dRes.data || []);
    } catch (e) { console.error(e); }
    finally { setIsLoading(false); hideLoader(); }
  };


  const loadGroupes = async () => {
    setLoadingGroupes(true);
    try {
      const r = await api.groupesSalariaux.getAll();
      if (r.success) setGroupes(r.data);
    } catch (e) { console.error(e); }
    finally { setLoadingGroupes(false); }
  };

  const loadPaliers = async () => {
    try {
      const r = await api.paliersDeduction.getAll();
      if (r.success) setPaliers(r.data);
    } catch (e) { console.error(e); }
  };

  const handleSaveGroupe = async (groupe) => {
    try {
      const r = groupe.id
        ? await api.groupesSalariaux.update(groupe.id, groupe)
        : await api.groupesSalariaux.create(groupe);
      if (r.success) { showToast('Groupe enregistré'); loadGroupes(); setEditingGroupe(null); setShowNewGroupe(false); setNewGroupe({ nom: '', description: '', taux_horaire: '', heures_journalieres: 8 }); }
      else showToast(r.message || 'Erreur', 'error');
    } catch (e) { showToast('Erreur', 'error'); }
  };

  const handleDeleteGroupe = async (id) => {
    if (!window.confirm('Supprimer ce groupe ?')) return;
    try {
      const r = await api.groupesSalariaux.delete(id);
      if (r.success) { showToast('Groupe supprimé'); loadGroupes(); }
      else showToast(r.message || 'Erreur', 'error');
    } catch (e) { showToast('Erreur', 'error'); }
  };

  const handleSavePalier = async () => {
    try {
      const r = await api.paliersDeduction.create(newPalier);
      if (r.success) { showToast('Palier enregistré'); loadPaliers(); setShowNewPalier(false); setNewPalier({ label: '', minutes_min: '', minutes_max: '', type_deduction: 'montant_fixe', valeur: '' }); }
      else showToast(r.message || 'Erreur', 'error');
    } catch (e) { showToast('Erreur', 'error'); }
  };

  const handleDeletePalier = async (id) => {
    if (!window.confirm('Supprimer ce palier ?')) return;
    try {
      const r = await api.paliersDeduction.delete(id);
      if (r.success) { showToast('Palier supprimé'); loadPaliers(); }
      else showToast(r.message || 'Erreur', 'error');
    } catch (e) { showToast('Erreur', 'error'); }
  };



  const updateParam = (key, value) => {
    setParams(p => ({ ...p, [key]: value }));
    setHasChanges(true);
  };

  const toBool = (v) => v === true || v === 'true';

  const handleSave = async () => {
    setIsSaving(true); showLoader();
    try {
      const toSend = {};
      for (const [k, v] of Object.entries(params)) toSend[k] = String(v);
      const r = await api.parametresCompta.update(toSend);
      if (r.success) { showToast(r.message); setHasChanges(false); await refreshContext(); }
      else showToast(r.message || 'Erreur', 'error');
    } catch (e) { showToast('Erreur', 'error'); }
    finally { setIsSaving(false); hideLoader(); }
  };

  // ============================================================================
  // SOUS-COMPOSANTS
  // ============================================================================
  const Field = ({ label, description, children, full }) => (
    <div className={full ? 'col-span-2' : ''}>
      <label className="block text-sm font-bold text-gray-800 mb-1.5">{label}</label>
      {description && <p className="text-xs text-gray-400 mb-2.5 leading-relaxed">{description}</p>}
      {children}
    </div>
  );

  const inputCls = 'w-full px-4 py-3 border border-gray-200 rounded-xl text-sm font-medium transition-all focus:outline-none focus:border-[#448696] focus:shadow-[0_0_0_3px_rgba(92,149,163,0.1)]';

  const Toggle = ({ value, onChange, label, description }) => (
    <div className="flex items-center justify-between bg-white rounded-xl border border-gray-100 px-5 py-4"
      style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
      <div className="flex-1 mr-6">
        <div className="text-sm font-bold text-gray-800">{label}</div>
        {description && <div className="text-xs text-gray-400 mt-0.5 leading-relaxed">{description}</div>}
      </div>
      <button onClick={() => onChange(!value)}
        className="relative w-12 h-7 rounded-full transition-colors duration-300 flex-shrink-0 focus:outline-none"
        style={{ backgroundColor: value ? '#448696' : '#d1d5db' }}>
        <div className="absolute top-1 w-5 h-5 bg-white rounded-full shadow-md transition-transform duration-300"
          style={{ transform: value ? 'translateX(24px)' : 'translateX(4px)' }} />
      </button>
    </div>
  );

  // ============================================================================
  // SÉLECTEUR DEVISE
  // ============================================================================
  const selectedDevise = devises.find(d => d.code === params.devise);
  const filteredDevises = devises.filter(d =>
    !deviseSearch || d.code.toLowerCase().includes(deviseSearch.toLowerCase()) ||
    d.nom.toLowerCase().includes(deviseSearch.toLowerCase()) ||
    (d.pays || '').toLowerCase().includes(deviseSearch.toLowerCase())
  );

  const selectDevise = (d) => {
    updateParam('devise', d.code);
    updateParam('symbole_devise', d.symbole);
    updateParam('decimales', d.decimales);
    setShowDeviseDropdown(false);
    setDeviseSearch('');
  };

  const DeviseSelector = () => (
    <div className="relative" ref={dropdownRef}>
      <button onClick={() => setShowDeviseDropdown(!showDeviseDropdown)}
        className="w-full flex items-center gap-4 px-5 py-4 bg-white border border-gray-200 rounded-xl hover:border-[#448696] transition-all group"
        style={showDeviseDropdown ? { borderColor: '#448696', boxShadow: '0 0 0 3px rgba(92,149,163,0.1)' } : {}}>
        <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: '#448696', boxShadow: '0 4px 12px rgba(92,149,163,0.25)' }}>
          <span className="text-lg font-bold text-white">{selectedDevise?.symbole || '?'}</span>
        </div>
        <div className="text-left flex-1">
          <div className="text-base font-bold text-gray-900">{selectedDevise?.code || params.devise || '—'}</div>
          <div className="text-xs text-gray-400 font-medium">{selectedDevise?.nom || 'Sélectionner une devise'}</div>
        </div>
        {selectedDevise?.pays && <div className="text-xs text-gray-300 font-medium max-w-[200px] truncate hidden lg:block">{selectedDevise.pays}</div>}
        <ChevronDown size={18} className={`text-gray-400 transition-transform duration-200 ${showDeviseDropdown ? 'rotate-180' : ''}`} />
      </button>

      {showDeviseDropdown && (
        <div className="absolute z-50 mt-2 w-full bg-white rounded-2xl border border-gray-200 overflow-hidden"
          style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
          {/* Search */}
          <div className="p-4 border-b border-gray-100 bg-gray-50">
            <div className="relative">
              <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input type="text" placeholder="Rechercher par nom, code ou pays..." value={deviseSearch} onChange={(e) => setDeviseSearch(e.target.value)} autoFocus
                className="w-full pl-10 pr-10 py-2.5 border border-gray-200 rounded-xl text-sm font-medium focus:outline-none focus:border-[#448696] bg-white" />
              {deviseSearch && (
                <button onClick={() => setDeviseSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500">
                  <X size={14} />
                </button>
              )}
            </div>
          </div>

          {/* List */}
          <div className="overflow-y-auto max-h-72">
            {filteredDevises.filter(d => d.est_favori).length > 0 && (
              <>
                <div className="px-5 py-2.5 text-[10px] font-extrabold uppercase tracking-[0.15em] text-gray-400 bg-gray-50/80 flex items-center gap-1.5 sticky top-0">
                  <Star size={10} className="text-amber-400" /> Devises populaires
                </div>
                {filteredDevises.filter(d => d.est_favori).map(d => (
                  <DeviseRow key={d.code} d={d} selected={params.devise === d.code} onSelect={() => selectDevise(d)} />
                ))}
              </>
            )}
            {filteredDevises.filter(d => !d.est_favori).length > 0 && (
              <>
                <div className="px-5 py-2.5 text-[10px] font-extrabold uppercase tracking-[0.15em] text-gray-400 bg-gray-50/80 sticky top-0">Toutes les devises</div>
                {filteredDevises.filter(d => !d.est_favori).map(d => (
                  <DeviseRow key={d.code} d={d} selected={params.devise === d.code} onSelect={() => selectDevise(d)} />
                ))}
              </>
            )}
            {filteredDevises.length === 0 && (
              <div className="p-8 text-center text-sm text-gray-400">Aucune devise trouvée</div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const DeviseRow = ({ d, selected, onSelect }) => (
    <button onClick={onSelect}
      className={`w-full flex items-center gap-4 px-5 py-3 transition-colors ${selected ? 'bg-[#f0f9fb]' : 'hover:bg-gray-50'}`}>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${selected ? 'bg-[#448696] text-white' : 'bg-gray-100 text-gray-500'}`}>
        <span className="text-sm font-bold">{d.symbole}</span>
      </div>
      <div className="text-left flex-1 min-w-0">
        <div className="text-sm font-bold text-gray-900">
          {d.code} <span className="font-medium text-gray-400">— {d.nom}</span>
        </div>
        <div className="text-[11px] text-gray-300 truncate">{d.pays}</div>
      </div>
      {selected && <Check size={18} className="text-[#448696] shrink-0" />}
    </button>
  );

  // ============================================================================
  // SECTIONS CONTENT
  // ============================================================================
  const renderContent = () => {
    switch (activeSection) {
      case 'general':
        return (
          <div className="space-y-8">
            {/* Devise — bloc spécial */}
            <div>
              <h3 className="text-sm font-bold text-gray-800 mb-1.5">Devise principale</h3>
              <p className="text-xs text-gray-400 mb-3">Devise utilisée pour tous les montants, factures et rapports</p>
              <DeviseSelector />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <Field label="Symbole affiché" description="Symbole court affiché après les montants">
                <input type="text" value={params.symbole_devise || ''} onChange={(e) => updateParam('symbole_devise', e.target.value)}
                  className={`${inputCls} font-mono font-bold text-center`} style={{ maxWidth: '120px' }} />
              </Field>
              <Field label="Décimales" description="Chiffres après la virgule">
                <input type="number" value={params.decimales ?? ''} onChange={(e) => updateParam('decimales', parseInt(e.target.value) || 0)} min={0} max={4}
                  className={`${inputCls} text-center`} style={{ maxWidth: '120px' }} />
              </Field>
              <Field label="Séparateur milliers" description="Espace, point ou virgule">
                <input type="text" value={params.separateur_milliers || ''} onChange={(e) => updateParam('separateur_milliers', e.target.value)}
                  className={`${inputCls} font-mono text-center`} style={{ maxWidth: '120px' }} />
              </Field>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Début d'exercice" description="Jour et mois de début (MM-JJ)">
                <input type="text" value={params.debut_exercice || ''} onChange={(e) => updateParam('debut_exercice', e.target.value)} placeholder="01-01"
                  className={`${inputCls} font-mono font-bold`} style={{ maxWidth: '160px' }} />
              </Field>
              <Field label="Fin d'exercice" description="Jour et mois de fin (MM-JJ)">
                <input type="text" value={params.fin_exercice || ''} onChange={(e) => updateParam('fin_exercice', e.target.value)} placeholder="12-31"
                  className={`${inputCls} font-mono font-bold`} style={{ maxWidth: '160px' }} />
              </Field>
            </div>
          </div>
        );

      case 'numerotation': {
        const PREFIXES = [
          { key: 'prefixe_facture', label: 'Factures', defaut: 'FAC', icon: '📄' },
          { key: 'prefixe_devis', label: 'Devis', defaut: 'DEV', icon: '📋' },
          { key: 'prefixe_bc_achat', label: 'BC Achat', defaut: 'BCA', icon: '🛒' },
          { key: 'prefixe_bc_vente', label: 'BC Vente', defaut: 'BCV', icon: '📦' },
          { key: 'prefixe_livraison', label: 'Livraisons', defaut: 'LIV', icon: '🚚' },
        ];
        const allDefault = PREFIXES.every(p => params[p.key] === p.defaut);
        const hasAnyPrefix = PREFIXES.some(p => params[p.key]);
        const year = new Date().getFullYear();

        const applyDefaults = () => {
          PREFIXES.forEach(p => updateParam(p.key, p.defaut));
        };

        return (
          <div className="space-y-6">
            {/* Bloc action */}
            <div className="flex items-center justify-between p-4 rounded-xl border border-gray-100 bg-gray-50">
              <div>
                <div className="text-sm font-bold text-gray-800">Préfixes de numérotation</div>
                <div className="text-xs text-gray-400 mt-0.5">
                  {hasAnyPrefix ? 'Modifiez les préfixes ou réinitialisez aux valeurs par défaut' : 'Commencez par générer les préfixes par défaut ou créez les vôtres'}
                </div>
              </div>
              <button onClick={applyDefaults} disabled={allDefault}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all disabled:opacity-30"
                style={{ backgroundColor: allDefault ? '#e5e7eb' : '#448696', color: allDefault ? '#9ca3af' : '#fff', boxShadow: allDefault ? 'none' : '0 4px 12px rgba(92,149,163,0.25)' }}>
                <RefreshCw size={15} />
                {hasAnyPrefix ? 'Réinitialiser par défaut' : 'Générer par défaut'}
              </button>
            </div>

            {/* Grille des préfixes */}
            <div className="space-y-3">
              {PREFIXES.map(p => {
                const val = params[p.key] || '';
                const preview = val ? `${val}-${year}-0001` : '—';
                const isDefault = val === p.defaut;
                return (
                  <div key={p.key} className="flex items-center gap-4 bg-white rounded-xl border border-gray-100 px-5 py-4"
                    style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
                    <span className="text-xl w-8 text-center">{p.icon}</span>
                    <div className="w-28 shrink-0">
                      <div className="text-sm font-bold text-gray-800">{p.label}</div>
                    </div>
                    <input type="text" value={val} onChange={(e) => updateParam(p.key, e.target.value.toUpperCase())} placeholder={p.defaut}
                      className="w-32 px-4 py-2.5 border border-gray-200 rounded-xl font-mono font-bold text-sm uppercase text-center transition-all focus:outline-none focus:border-[#448696] focus:shadow-[0_0_0_3px_rgba(92,149,163,0.1)]" />
                    <div className="flex-1 flex items-center gap-3">
                      <span className="text-gray-300">→</span>
                      <span className={`font-mono text-sm font-bold ${val ? 'text-gray-700' : 'text-gray-300'}`}>{preview}</span>
                    </div>
                    {isDefault && (
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md bg-green-50 text-green-600">défaut</span>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Toggle RAZ */}
            <Toggle label="Remise à zéro annuelle" description="Réinitialiser les compteurs de numérotation au 1er janvier de chaque année"
              value={toBool(params.raz_annuelle)} onChange={(v) => updateParam('raz_annuelle', v)} />

            {/* Info */}
            <div className="flex items-start gap-3 p-4 rounded-xl" style={{ backgroundColor: '#f0f9fb', border: '1px solid #d4eef4' }}>
              <Info size={16} className="mt-0.5 shrink-0" style={{ color: '#448696' }} />
              <div className="text-xs text-gray-600 leading-relaxed">
                Le format final est <b className="font-mono">[PRÉFIXE]-[ANNÉE]-[NUMÉRO]</b>. Le numéro s'incrémente automatiquement.
                {toBool(params.raz_annuelle) && <span> Les compteurs repartiront à 0001 au 1er janvier {year + 1}.</span>}
              </div>
            </div>
          </div>
        );
      }

      case 'facturation':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="TVA par défaut" description="Taux appliqué automatiquement aux nouvelles factures">
                <div className="flex items-center gap-2">
                  <input type="number" value={params.tva_defaut ?? ''} onChange={(e) => updateParam('tva_defaut', parseFloat(e.target.value) || 0)} min={0} max={100}
                    className={`${inputCls} text-center`} style={{ maxWidth: '120px' }} />
                  <span className="text-sm font-bold text-gray-400">%</span>
                </div>
              </Field>
              <Field label="Délai de paiement" description="Nombre de jours à partir de la date d'émission">
                <div className="flex items-center gap-2">
                  <input type="number" value={params.delai_paiement_jours ?? ''} onChange={(e) => updateParam('delai_paiement_jours', parseInt(e.target.value) || 0)} min={0}
                    className={`${inputCls} text-center`} style={{ maxWidth: '120px' }} />
                  <span className="text-sm font-bold text-gray-400">jours</span>
                </div>
              </Field>
            </div>

            <Field label="Mentions légales" description="Texte affiché en pied de chaque facture et devis" full>
              <textarea value={params.mentions_legales || ''} onChange={(e) => updateParam('mentions_legales', e.target.value)} rows="3"
                placeholder="Ex: Conditions de paiement, pénalités de retard..."
                className={inputCls} />
            </Field>

            <div className="space-y-3">
              <Toggle label="Pénalités de retard" description="Activer le calcul et l'affichage des pénalités de retard sur les factures"
                value={toBool(params.penalites_retard_actif)} onChange={(v) => updateParam('penalites_retard_actif', v)} />

              {toBool(params.penalites_retard_actif) && (
                <div className="pl-5 border-l-2" style={{ borderColor: '#448696' }}>
                  <Field label="Taux de pénalité">
                    <div className="flex items-center gap-2">
                      <input type="number" value={params.penalites_retard_taux ?? ''} onChange={(e) => updateParam('penalites_retard_taux', parseFloat(e.target.value) || 0)} min={0}
                        className={`${inputCls} text-center`} style={{ maxWidth: '120px' }} />
                      <span className="text-sm font-bold text-gray-400">% / mois</span>
                    </div>
                  </Field>
                </div>
              )}
            </div>
          </div>
        );

      case 'tresorerie':
        return (
          <div className="space-y-4">
            <Toggle label="Autoriser les soldes négatifs" description="Permettre aux caisses et portefeuilles de passer en dessous de zéro"
              value={toBool(params.autoriser_solde_negatif)} onChange={(v) => updateParam('autoriser_solde_negatif', v)} />
            <Toggle label="Validation des transferts" description="Exiger une approbation avant tout transfert inter-caisses"
              value={toBool(params.validation_transfert)} onChange={(v) => updateParam('validation_transfert', v)} />
          </div>
        );

      case 'stocks':
        return (
          <div className="space-y-5">
            <Toggle label="Gestion de stock activée" description="Activer le suivi des quantités en stock et les mouvements"
              value={toBool(params.gestion_stock_active)} onChange={(v) => updateParam('gestion_stock_active', v)} />
            <Toggle label="Alerte stock minimum" description="Recevoir une alerte quand un article passe sous le seuil défini"
              value={toBool(params.alerte_stock_minimum)} onChange={(v) => updateParam('alerte_stock_minimum', v)} />
            <Toggle label="Autoriser stock négatif" description="Permettre les sorties de stock même si la quantité disponible est à zéro"
              value={toBool(params.autoriser_stock_negatif)} onChange={(v) => updateParam('autoriser_stock_negatif', v)} />

            <Field label="Méthode de valorisation" description="Méthode de calcul du coût unitaire des articles en stock">
              <select value={params.methode_valorisation || 'cmup'} onChange={(e) => updateParam('methode_valorisation', e.target.value)}
                className={inputCls} style={{ maxWidth: '400px' }}>
                {METHODES_VALORISATION.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </Field>
          </div>
        );

      case 'commandes':
        return (
          <div className="space-y-4">
            <Toggle label="Validation obligatoire" description="Exiger une approbation avant de confirmer un bon de commande"
              value={toBool(params.validation_obligatoire)} onChange={(v) => updateParam('validation_obligatoire', v)} />
            <Toggle label="Conversion automatique en facture" description="Générer automatiquement une facture lorsque la commande est entièrement livrée"
              value={toBool(params.conversion_auto_facture)} onChange={(v) => updateParam('conversion_auto_facture', v)} />
            <Toggle label="Relance fournisseur" description="Envoyer des rappels automatiques pour les commandes en attente de livraison"
              value={toBool(params.relance_fournisseur_actif)} onChange={(v) => updateParam('relance_fournisseur_actif', v)} />

            {toBool(params.relance_fournisseur_actif) && (
              <div className="pl-5 border-l-2" style={{ borderColor: '#ec4899' }}>
                <Field label="Délai avant relance">
                  <div className="flex items-center gap-2">
                    <input type="number" value={params.relance_fournisseur_jours ?? ''} onChange={(e) => updateParam('relance_fournisseur_jours', parseInt(e.target.value) || 1)} min={1}
                      className={`${inputCls} text-center`} style={{ maxWidth: '120px' }} />
                    <span className="text-sm font-bold text-gray-400">jours</span>
                  </div>
                </Field>
              </div>
            )}
          </div>
        );

      case 'paie':
        return (
          <div className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">

             <Field label="Nom de la cotisation sociale" description="Ex: CNSS, INSS, Sécurité sociale, Cotisation retraite...">
                <input type="text" value={params.nom_cotisation_sociale ?? 'CNSS'} 
                  onChange={(e) => updateParam('nom_cotisation_sociale', e.target.value)}
                  className={inputCls} style={{ maxWidth: '250px' }}
                  placeholder="CNSS" />
              </Field>
              <Field label="Taux de cotisation sociale" description="Laisser à 0 si non applicable">
                <div className="flex items-center gap-2">
                  <input type="number" value={params.taux_cnss ?? ''} 
                    onChange={(e) => updateParam('taux_cnss', parseFloat(e.target.value) || 0)} 
                    min={0} className={`${inputCls} text-center`} style={{ maxWidth: '120px' }} />
                  <span className="text-sm font-bold text-gray-400">%</span>
                </div>
              </Field>

              <Field label="Plafond avances" description="Maximum autorisé en % du salaire">
                <div className="flex items-center gap-2">
                  <input type="number" value={params.plafond_avance_pourcent ?? ''} onChange={(e) => updateParam('plafond_avance_pourcent', parseInt(e.target.value) || 0)} min={0} max={100}
                    className={`${inputCls} text-center`} style={{ maxWidth: '120px' }} />
                  <span className="text-sm font-bold text-gray-400">%</span>
                </div>
              </Field>
              <Field label="Jour de paie" description="Jour du mois pour le versement">
                <div className="flex items-center gap-2">
                  <input type="number" value={params.jour_paie ?? ''} onChange={(e) => updateParam('jour_paie', parseInt(e.target.value) || 1)} min={1} max={31}
                    className={`${inputCls} text-center`} style={{ maxWidth: '120px' }} />
                  <span className="text-sm font-bold text-gray-400">du mois</span>
                </div>
              </Field>
            </div>
          </div>
        );

      case 'validation':
        return (
          <div className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Seuil d'approbation" description="Montant au-delà duquel une validation est obligatoire (0 = toujours)">
                <div className="flex items-center gap-2">
                  <input type="number" value={params.seuil_approbation ?? ''} onChange={(e) => updateParam('seuil_approbation', parseInt(e.target.value) || 0)} min={0}
                    className={`${inputCls} text-center`} style={{ maxWidth: '180px' }} />
                  <span className="text-sm font-bold text-gray-400">{params.symbole_devise || 'F'}</span>
                </div>
              </Field>
              <Field label="Niveaux d'approbation" description="Nombre d'approbateurs requis successivement">
                <div className="flex items-center gap-2">
                  <input type="number" value={params.niveaux_approbation ?? ''} onChange={(e) => updateParam('niveaux_approbation', parseInt(e.target.value) || 1)} min={1} max={3}
                    className={`${inputCls} text-center`} style={{ maxWidth: '120px' }} />
                  <span className="text-sm font-bold text-gray-400">niveau(x)</span>
                </div>
              </Field>
            </div>

            <Toggle label="Notification email" description="Envoyer un email à l'approbateur lorsqu'une demande de validation est soumise"
              value={toBool(params.notification_email)} onChange={(v) => updateParam('notification_email', v)} />
          </div>
        );

        case 'groupes_salariaux':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Définissez les catégories de taux horaire. Les employés peuvent être assignés à un groupe ou avoir un taux personnalisé.</p>
              </div>
              <button onClick={() => setShowNewGroupe(true)}
                className="flex items-center gap-2 px-4 py-2.5 text-white rounded-xl text-sm font-bold transition-all"
                style={{ backgroundColor: '#448696' }}>
                <Plus size={16} /> Nouveau groupe
              </button>
            </div>

            {/* Formulaire nouveau groupe */}
            {showNewGroupe && (
              <div className="p-5 rounded-xl border-2 space-y-4" style={{ borderColor: '#448696', backgroundColor: '#f0f9fb' }}>
                <h4 className="text-sm font-bold text-gray-800">Nouveau groupe salarial</h4>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Nom du groupe">
                    <input type="text" value={newGroupe.nom} onChange={e => setNewGroupe(p => ({ ...p, nom: e.target.value }))}
                      placeholder="Ex: Ouvriers catégorie A" className={inputCls} />
                  </Field>
                  <Field label="Taux horaire (FCFA)">
                    <input type="number" value={newGroupe.taux_horaire} onChange={e => setNewGroupe(p => ({ ...p, taux_horaire: e.target.value }))}
                      placeholder="Ex: 2000" className={inputCls} />
                  </Field>
                  <Field label="Heures journalières théoriques">
                    <div className="flex items-center gap-2">
                      <input type="number" value={newGroupe.heures_journalieres} onChange={e => setNewGroupe(p => ({ ...p, heures_journalieres: e.target.value }))}
                        min={1} max={24} className={inputCls} style={{ maxWidth: '120px' }} />
                      <span className="text-sm font-bold text-gray-400">h/jour</span>
                    </div>
                  </Field>
                  <Field label="Description (optionnel)">
                    <input type="text" value={newGroupe.description} onChange={e => setNewGroupe(p => ({ ...p, description: e.target.value }))}
                      placeholder="Ex: Ouvriers du chantier port" className={inputCls} />
                  </Field>
                </div>
                <div className="flex gap-3 justify-end">
                  <button onClick={() => { setShowNewGroupe(false); setNewGroupe({ nom: '', description: '', taux_horaire: '', heures_journalieres: 8 }); }}
                    className="px-4 py-2 rounded-xl text-sm font-semibold border border-gray-200 text-gray-600 hover:bg-gray-50">
                    Annuler
                  </button>
                  <button onClick={() => handleSaveGroupe(newGroupe)}
                    className="px-4 py-2 rounded-xl text-sm font-bold text-white" style={{ backgroundColor: '#448696' }}>
                    Enregistrer
                  </button>
                </div>
              </div>
            )}

            {/* Liste des groupes */}
            {loadingGroupes ? null : groupes.length === 0 ? (
              <div className="text-center py-10 text-gray-400">
                <Users size={32} className="mx-auto mb-2 opacity-30" />
                <p className="text-sm font-medium">Aucun groupe salarial défini</p>
                <p className="text-xs mt-1">Créez des groupes pour organiser vos employés par taux horaire</p>
              </div>
            ) : (
              <div className="space-y-3">
                {groupes.map(g => (
                  <div key={g.id}>
                    {editingGroupe?.id === g.id ? (
                      <div className="p-5 rounded-xl border-2 space-y-4" style={{ borderColor: '#448696', backgroundColor: '#f0f9fb' }}>
                        <div className="grid grid-cols-2 gap-4">
                          <Field label="Nom du groupe">
                            <input type="text" value={editingGroupe.nom} onChange={e => setEditingGroupe(p => ({ ...p, nom: e.target.value }))} className={inputCls} />
                          </Field>
                          <Field label="Taux horaire (FCFA)">
                            <input type="number" value={editingGroupe.taux_horaire} onChange={e => setEditingGroupe(p => ({ ...p, taux_horaire: e.target.value }))} className={inputCls} />
                          </Field>
                          <Field label="Heures journalières">
                            <div className="flex items-center gap-2">
                              <input type="number" value={editingGroupe.heures_journalieres} onChange={e => setEditingGroupe(p => ({ ...p, heures_journalieres: e.target.value }))}
                                min={1} max={24} className={inputCls} style={{ maxWidth: '120px' }} />
                              <span className="text-sm font-bold text-gray-400">h/jour</span>
                            </div>
                          </Field>
                          <Field label="Description">
                            <input type="text" value={editingGroupe.description || ''} onChange={e => setEditingGroupe(p => ({ ...p, description: e.target.value }))} className={inputCls} />
                          </Field>
                        </div>
                        <div className="flex gap-3 justify-end">
                          <button onClick={() => setEditingGroupe(null)} className="px-4 py-2 rounded-xl text-sm font-semibold border border-gray-200 text-gray-600 hover:bg-gray-50">Annuler</button>
                          <button onClick={() => handleSaveGroupe(editingGroupe)} className="px-4 py-2 rounded-xl text-sm font-bold text-white" style={{ backgroundColor: '#448696' }}>Enregistrer</button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-4 bg-white rounded-xl border border-gray-100 px-5 py-4" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#448696' }}>
                          <Users size={18} className="text-white" />
                        </div>
                        <div className="flex-1">
                          <div className="text-sm font-bold text-gray-900">{g.nom}</div>
                          {g.description && <div className="text-xs text-gray-400">{g.description}</div>}
                        </div>
                        <div className="text-right mr-4">
                          <div className="text-sm font-bold" style={{ color: '#448696' }}>{parseFloat(g.taux_horaire).toLocaleString('fr-FR')} FCFA/h</div>
                          <div className="text-xs text-gray-400">{g.heures_journalieres}h/jour théoriques</div>
                        </div>
                        <div className="flex gap-2">
                          <button onClick={() => setEditingGroupe({ ...g })} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-all">
                            <Edit2 size={15} />
                          </button>
                          <button onClick={() => handleDeleteGroupe(g.id)} className="p-2 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-all">
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );

     case 'deductions_retard':
        return (
          <div className="space-y-6">
            <div className="flex items-start justify-between">
              <div className="flex-1 mr-4">
                <p className="text-sm text-gray-500 leading-relaxed">
                  Définissez le montant déduit pour chaque statut de ponctualité sur les employés payés à l'heure.
                  Ces statuts sont calculés automatiquement depuis vos <strong>règles de ponctualité</strong> configurées dans les paramètres de pointage.
                </p>
              </div>
              <button onClick={() => setShowNewPalier(true)}
                className="flex items-center gap-2 px-4 py-2.5 text-white rounded-xl text-sm font-bold transition-all flex-shrink-0"
                style={{ backgroundColor: '#f97316' }}>
                <Plus size={16} /> Nouveau palier
              </button>
            </div>

            {/* Lien vers paramètres pointage */}
            <div className="flex items-start gap-3 p-4 rounded-xl" style={{ backgroundColor: '#f0f9fb', border: '1px solid #c8dde2' }}>
              <Info size={16} className="mt-0.5 shrink-0" style={{ color: '#448696' }} />
              <div className="text-xs text-gray-600 leading-relaxed">
                <strong>Coopération avec les paramètres de pointage :</strong> Les statuts ci-dessous
                (tolérance, retard, retard grave...) sont exactement les mêmes que ceux définis dans vos règles
                de ponctualité. Quand un employé pointe avec ce statut, la déduction configurée ici
                est automatiquement appliquée sur son salaire horaire.
              </div>
            </div>

            {/* Explication */}
            <div className="flex items-start gap-3 p-4 rounded-xl" style={{ backgroundColor: '#fff7ed', border: '1px solid #fdba74' }}>
              <Info size={16} className="mt-0.5 shrink-0 text-orange-500" />
              <div className="text-xs text-orange-700 leading-relaxed">
                <strong>Comment ça fonctionne :</strong> Pour chaque pointage d'un employé horaire,
                si le statut de ponctualité correspond à un palier configuré, le montant ou pourcentage
                est déduit du calcul de cette journée. Un statut sans palier = aucune déduction.
              </div>
            </div>

            {/* Formulaire nouveau palier */}
            {showNewPalier && (
              <div className="p-5 rounded-xl border-2 space-y-4" style={{ borderColor: '#f97316', backgroundColor: '#fff7ed' }}>
                <h4 className="text-sm font-bold text-gray-800">Nouveau palier de déduction</h4>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Libellé" full>
                    <input type="text" value={newPalier.label}
                      onChange={e => setNewPalier(p => ({ ...p, label: e.target.value }))}
                      placeholder="Ex: Déduction retard simple" className={inputCls} />
                  </Field>
                  <Field label="Statut de ponctualité concerné">
                    <select value={newPalier.statut_ponctualite}
                      onChange={e => setNewPalier(p => ({ ...p, statut_ponctualite: e.target.value }))}
                      className={inputCls}>
                      <option value="">-- Sélectionner un statut --</option>
                      <option value="tolerance">Tolérance</option>
                      <option value="retard">Retard</option>
                      <option value="retard_grave">Retard grave</option>
                      <option value="depart_anticipe">Départ anticipé</option>
                      <option value="depart_anticipe_grave">Départ anticipé grave</option>
                      <option value="pause_excessive">Pause excessive</option>
                    </select>
                  </Field>
                  <Field label="Type de déduction">
                    <select value={newPalier.type_deduction}
                      onChange={e => setNewPalier(p => ({ ...p, type_deduction: e.target.value }))}
                      className={inputCls}>
                      <option value="montant_fixe">Montant fixe</option>
                      <option value="pourcentage_taux_horaire">% du taux horaire</option>
                    </select>
                  </Field>
                  <Field label={newPalier.type_deduction === 'montant_fixe' ? `Montant à déduire (${symbole})` : 'Pourcentage à déduire (%)'}>
                    <div className="flex items-center gap-2">
                      <input type="number" value={newPalier.valeur}
                        onChange={e => setNewPalier(p => ({ ...p, valeur: e.target.value }))}
                        placeholder="Ex: 500" min={0} className={inputCls} style={{ maxWidth: '150px' }} />
                      <span className="text-sm font-bold text-gray-400">
                        {newPalier.type_deduction === 'montant_fixe' ? symbole : '%'}
                      </span>
                    </div>
                  </Field>
                </div>
                <div className="flex gap-3 justify-end">
                  <button onClick={() => { setShowNewPalier(false); setNewPalier({ label: '', statut_ponctualite: '', type_deduction: 'montant_fixe', valeur: '' }); }}
                    className="px-4 py-2 rounded-xl text-sm font-semibold border border-gray-200 text-gray-600 hover:bg-gray-50">
                    Annuler
                  </button>
                  <button onClick={handleSavePalier}
                    className="px-4 py-2 rounded-xl text-sm font-bold text-white" style={{ backgroundColor: '#f97316' }}>
                    Enregistrer
                  </button>
                </div>
              </div>
            )}

            {/* Badges statuts disponibles */}
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Statuts de ponctualité disponibles</p>
              <div className="flex flex-wrap gap-2">
                {[
                  { key: 'tolerance',            label: 'Tolérance',             color: '#d97706', bg: '#fffbeb' },
                  { key: 'retard',               label: 'Retard',                color: '#f97316', bg: '#fff7ed' },
                  { key: 'retard_grave',         label: 'Retard grave',          color: '#dc2626', bg: '#fef2f2' },
                  { key: 'depart_anticipe',      label: 'Départ anticipé',       color: '#d97706', bg: '#fffbeb' },
                  { key: 'depart_anticipe_grave',label: 'Départ anticipé grave', color: '#dc2626', bg: '#fef2f2' },
                  { key: 'pause_excessive',      label: 'Pause excessive',       color: '#7c3aed', bg: '#f5f3ff' },
                ].map(s => {
                  const palierExistant = paliers.find(p => p.statut_ponctualite === s.key);
                  return (
                    <div key={s.key} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border"
                      style={{ backgroundColor: s.bg, color: s.color, borderColor: s.color + '30' }}>
                      {palierExistant ? '✓' : '○'} {s.label}
                      {palierExistant && (
                        <span className="ml-1 font-bold">
                          ({palierExistant.type_deduction === 'montant_fixe'
                            ? `-${parseFloat(palierExistant.valeur).toLocaleString('fr-FR')} ${symbole}`
                            : `-${palierExistant.valeur}%`})
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Liste des paliers */}
            {paliers.length === 0 ? (
              <div className="text-center py-10 text-gray-400">
                <Clock size={32} className="mx-auto mb-2 opacity-30" />
                <p className="text-sm font-medium">Aucun palier défini</p>
                <p className="text-xs mt-1">Sans palier, aucune déduction n'est appliquée sur les retards</p>
              </div>
            ) : (
              <div className="space-y-3">
                {paliers.map(p => {
                  const statutInfo = {
                    tolerance:             { label: 'Tolérance',             color: '#d97706', bg: '#fffbeb' },
                    retard:                { label: 'Retard',                color: '#f97316', bg: '#fff7ed' },
                    retard_grave:          { label: 'Retard grave',          color: '#dc2626', bg: '#fef2f2' },
                    depart_anticipe:       { label: 'Départ anticipé',       color: '#d97706', bg: '#fffbeb' },
                    depart_anticipe_grave: { label: 'Départ anticipé grave', color: '#dc2626', bg: '#fef2f2' },
                    pause_excessive:       { label: 'Pause excessive',       color: '#7c3aed', bg: '#f5f3ff' },
                  }[p.statut_ponctualite] || { label: p.statut_ponctualite, color: '#6b7280', bg: '#f9fafb' };

                  return (
                    <div key={p.id} className="flex items-center gap-4 bg-white rounded-xl border border-gray-100 px-5 py-4"
                      style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-orange-100">
                        <Clock size={18} className="text-orange-600" />
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-bold text-gray-900">{p.label}</div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="px-2 py-0.5 rounded-full text-xs font-bold"
                            style={{ backgroundColor: statutInfo.bg, color: statutInfo.color }}>
                            {statutInfo.label}
                          </span>
                        </div>
                      </div>
                      <div className="text-right mr-4">
                        <div className="text-sm font-bold text-orange-600">
                          {p.type_deduction === 'montant_fixe'
                            ? `- ${parseFloat(p.valeur).toLocaleString('fr-FR')} ${symbole}`
                            : `- ${p.valeur}% du taux/h`}
                        </div>
                      </div>
                      <button onClick={() => handleDeletePalier(p.id)}
                        className="p-2 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-all">
                        <Trash2 size={15} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );

      case 'envoi_compta':
        return (
          <div className="space-y-5">
            <div className="flex items-start gap-3 p-4 rounded-xl" style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
              <Info size={16} className="mt-0.5 shrink-0 text-green-600" />
              <div className="text-xs text-green-700 leading-relaxed">
                <strong>Règle de blocage :</strong> Les pointages incomplets non justifiés ne peuvent jamais être envoyés à la comptabilité,
                quel que soit le mode d'envoi. Seuls les pointages avec statut <strong>OK</strong> ou <strong>Retard</strong> sont transmis.
              </div>
            </div>

            <Toggle
              label="Envoi automatique chaque soir"
              description="Les pointages OK et Retard du jour sont automatiquement envoyés à la comptabilité à l'heure définie"
              value={toBool(params.envoi_compta_auto)}
              onChange={(v) => updateParam('envoi_compta_auto', v)}
            />

            {toBool(params.envoi_compta_auto) && (
              <div className="pl-5 border-l-2" style={{ borderColor: '#10b981' }}>
                <Field label="Heure d'envoi automatique" description="Heure à laquelle les pointages sont automatiquement transmis">
                  <input type="time" value={params.envoi_compta_heure || '23:00'} onChange={(e) => updateParam('envoi_compta_heure', e.target.value)}
                    className={inputCls} style={{ maxWidth: '160px' }} />
                </Field>
              </div>
            )}

            <div className="p-4 rounded-xl border border-gray-100 bg-gray-50">
              <p className="text-xs font-semibold text-gray-600 mb-2">Récapitulatif des règles d'envoi</p>
              <div className="space-y-1.5">
                {[
                  { statut: '✅ OK', desc: 'Transmis automatiquement ou manuellement', color: '#22c55e' },
                  { statut: '⏰ Retard', desc: 'Transmis avec déduction retard appliquée', color: '#f97316' },
                  { statut: '❌ Incomplet', desc: 'Bloqué — doit être justifié d\'abord', color: '#ef4444' },
                  { statut: '❌ Absent', desc: 'Non transmis — 0 heure = 0 paiement', color: '#6b7280' },
                ].map(item => (
                  <div key={item.statut} className="flex items-center gap-3">
                    <span className="text-xs font-bold w-24 flex-shrink-0" style={{ color: item.color }}>{item.statut}</span>
                    <span className="text-xs text-gray-500">{item.desc}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      default: return null;
    }
  };

  // ============================================================================
  // RENDU
  // ============================================================================
  if (isLoading) return null;

  return (
    <div className="min-h-screen p-6" style={{ backgroundColor: '#fdfcf5' }}>
      {/* Bouton sauvegarder flottant */}
      {hasChanges && (
        <div className="fixed bottom-6 right-6 z-50 animate-[slideUp_0.3s_ease]">
          <button onClick={handleSave} disabled={isSaving}
            className="flex items-center gap-2.5 px-7 py-3.5 text-white rounded-2xl font-bold text-sm transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50"
            style={{ backgroundColor: '#448696', boxShadow: '0 8px 32px rgba(92,149,163,0.4)' }}>
            <Save size={18} />
            {isSaving ? 'Enregistrement...' : 'Enregistrer les modifications'}
          </button>
        </div>
      )}

      <div className="flex gap-6">
        {/* Sidebar sections */}
        <div className="w-72 shrink-0">
          <div className="sticky top-24 space-y-1.5">
            {SECTIONS.map(s => {
              const SIcon = s.icon;
              const active = activeSection === s.id;
              return (
                <button key={s.id} onClick={() => setActiveSection(s.id)}
                  className="w-full flex items-center gap-3.5 px-4 py-3.5 rounded-xl text-left transition-all"
                  style={{
                    backgroundColor: active ? '#fff' : 'transparent',
                    boxShadow: active ? '0 2px 12px rgba(0,0,0,0.06)' : 'none',
                    border: active ? '1px solid #f0f0ee' : '1px solid transparent',
                  }}
                  onMouseEnter={(e) => { if (!active) e.currentTarget.style.backgroundColor = '#fff'; }}
                  onMouseLeave={(e) => { if (!active) e.currentTarget.style.backgroundColor = 'transparent'; }}>
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 transition-all"
                    style={{ backgroundColor: active ? s.color : s.color + '12' }}>
                    <SIcon size={17} style={{ color: active ? '#fff' : s.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-bold truncate ${active ? 'text-gray-900' : 'text-gray-600'}`}>{s.title}</div>
                    <div className="text-[11px] text-gray-400 truncate">{s.subtitle}</div>
                  </div>
                  {active && <div className="w-1.5 h-8 rounded-full shrink-0" style={{ backgroundColor: s.color }} />}
                </button>
              );
            })}
          </div>
        </div>

        {/* Contenu */}
        <div className="flex-1 min-w-0">
          {/* Header section */}
          {(() => {
            const s = SECTIONS.find(x => x.id === activeSection);
            const SIcon = s?.icon || Settings;
            return (
              <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-6" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: s?.color, boxShadow: `0 4px 14px ${s?.color}30` }}>
                    <SIcon size={24} className="text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">{s?.title}</h2>
                    <p className="text-sm text-gray-400 font-medium">{s?.subtitle}</p>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Content */}
          <div className="bg-white rounded-2xl border border-gray-100 p-8" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            {renderContent()}
          </div>
        </div>
      </div>

      {toast.show && <Toast show={toast.show} message={toast.message} type={toast.type} onClose={() => setToast({ show: false, message: '', type: '' })} />}
    </div>
  );
};

export default ParametresComptabilite;